import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../../utils/api';
import { PartnerEndpoints } from '../../../../utils/endpoints';
import { yesterdayISO } from '../../../../utils/datetime';
import { requestMessage, validateParams } from './useDashboard';

export const TAP_CHART_METRICS = [
  { key: 'gross_volume', label: 'Gross volume', unit: 'currency' },
  { key: 'txn_count', label: 'Transactions', unit: 'count' },
];

const buildSeries = (series, metricKey) => {
  if (!series) return null;
  const metric = TAP_CHART_METRICS.find((option) => option.key === metricKey);
  return {
    label: metric.label,
    unit: metric.unit,
    points: (series.points ?? []).map((point) => ({ date: point.date, value: point[metricKey] })),
  };
};

const useTapDashboard = ({ endpoint = PartnerEndpoints.ownDashboard, scopeParam = 'pan' } = {}) => {
  const [params, setParams] = useState({
    granularity: 'monthly',
    date: yesterdayISO(),
    from: '',
    to: '',
    [scopeParam]: '',
  });
  const [metric, setMetric] = useState('gross_volume');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const latestRequest = useRef(0);

  const { granularity, date, from, to } = params;
  const scope = params[scopeParam];
  const paramError = validateParams(params);

  const updateParams = useCallback((patch) => setParams((current) => ({ ...current, ...patch })), []);
  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (paramError) {
      setLoading(false);
      return;
    }

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);
    setRequestError(null);

    const period = granularity === 'custom' ? { granularity, from, to } : { granularity, date };
    const query = scope ? { ...period, [scopeParam]: scope } : period;

    api
      .get(endpoint, { params: query })
      .then((response) => {
        if (latestRequest.current !== requestId) return;
        setData(response?.data?.data ?? null);
        setLoading(false);
      })
      .catch((failure) => {
        if (latestRequest.current !== requestId) return;
        setData(null);
        setError(requestMessage(failure));
        setRequestError(failure);
        setLoading(false);
      });
  }, [endpoint, scopeParam, granularity, date, from, to, scope, paramError, reloadToken]);

  return {
    params,
    updateParams,
    metric,
    setMetric,
    refetch,
    data,
    series: buildSeries(data?.series, metric),
    loading,
    error,
    requestError,
    paramError,
  };
};

export default useTapDashboard;
